import {ref} from 'vue';
import {useHttpRequest} from '~/composables/useHttpRequest';
import {endpoints} from '~/network/endpoints.js';

const FILTER_KEY = {1: 'ladderIds', 2: 'leagueCycleIds', 6: 'americanoCycleIds'};

/**
 * usePendingPlacements — the placement waiting area (JOIN_SETTINGS_PLAN.md §6):
 * list a competition's waiting players, suggested teams, place, dismiss/withdraw,
 * and a member's preferred partners.
 */
export function usePendingPlacements() {
    const {httpRequest} = useHttpRequest();
    const {items, getItems, listPending, deleteItem, formErrors, hasFormErrors} = useCrudApi('api/v2/pendingPlacements');

    const suggestions = ref(null);
    const actionPending = ref(false);
    const actionErrors = ref(null);

    const listFor = async (referenceType, referenceId) => {
        const key = FILTER_KEY[referenceType];
        if (!key) return [];
        await getItems(1, 200, {[key]: [referenceId]});
        return items.value ?? [];
    };

    const loadSuggestions = async (referenceType, referenceId) => {
        try {
            suggestions.value = await httpRequest(endpoints.pendingPlacements.suggested(referenceType, referenceId));
        } catch (e) {
            suggestions.value = null;
        }
        return suggestions.value;
    };

    const place = async (payload) => {
        actionPending.value = true;
        actionErrors.value = null;
        try {
            return await httpRequest(endpoints.pendingPlacements.place, payload);
        } catch (e) {
            actionErrors.value = e;
            return null;
        } finally {
            actionPending.value = false;
        }
    };

    // Admin dismiss or the member's own withdrawal — same DELETE.
    const dismiss = async (id) => {
        actionPending.value = true;
        actionErrors.value = null;
        try {
            await deleteItem(id);
            if (hasFormErrors.value) {
                actionErrors.value = formErrors.value;
                return false;
            }
            return true;
        } catch (e) {
            actionErrors.value = e;
            return false;
        } finally {
            actionPending.value = false;
        }
    };

    const setPreferredPartners = async (id, partnerPlayerIds) => {
        actionPending.value = true;
        actionErrors.value = null;
        try {
            return await httpRequest(endpoints.pendingPlacements.preferredPartners(id), {partnerPlayerIds});
        } catch (e) {
            actionErrors.value = e;
            return null;
        } finally {
            actionPending.value = false;
        }
    };

    // Admin: keep club members in a competition's waiting area — people just taken out
    // of it, or a walk-in. One request per player; returns the names the API refused.
    const REFERENCE_KEY = {1: 'ladderId', 2: 'leagueCycleId', 6: 'americanoCycleId'};
    const addToWaiting = async (referenceType, referenceId, players) => {
        const key = REFERENCE_KEY[referenceType];
        const failed = [];
        if (!key) return {added: 0, failed};
        let added = 0;
        for (const player of players) {
            try {
                await httpRequest(endpoints.pendingPlacements.addItem, {playerId: player.id, [key]: referenceId});
                added++;
            } catch (e) {
                failed.push({player, error: e?.global?.[0] ?? e?.exception ?? null});
            }
        }
        return {added, failed};
    };

    const mine = async () => {
        await getItems(1, 50, {mine: true});
        return items.value ?? [];
    };

    return {
        items, listPending, listFor, suggestions, loadSuggestions,
        place, dismiss, setPreferredPartners, mine, addToWaiting, actionPending, actionErrors,
    };
}
